// import * as XLSX from "xlsx";
import dayjs from "dayjs";
import {
  formatCurrency,
  formatDuration,
  formatNumber,
  formatPct,
  formatProductLabel,
} from "./chartHelpers";
import { filterSignupFunnelSteps } from "./funnelHelpers";

export const csvCell = (value) => {
  const text = value == null ? "" : String(value);
  if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

export const sanitizeSheetName = (name) =>
  String(name || "Sheet")
    .replace(/[\\/*?:[\]]/g, "")
    .slice(0, 31);

const objectRowsToMatrix = (headers, rows) => [
  headers,
  ...(rows || []).map((row) => headers.map((h) => row[h] ?? "")),
];

export const sheetToCsv = (sheet) => {
  const headers = sheet.headers || [];
  const matrix = objectRowsToMatrix(headers, sheet.rows);
  return matrix.map((line) => line.map(csvCell).join(",")).join("\n");
};

export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export const downloadCsvSheet = (sheet, filename) => {
  if (!sheet?.rows?.length) return;
  const csv = sheetToCsv(sheet);
  downloadBlob(new Blob([csv], { type: "text/csv;charset=utf-8;" }), filename);
};

export const downloadExcelWorkbook = (sheets, filename) => {
  const valid = (sheets || []).filter((s) => s?.rows?.length);
  if (!valid.length) return;

  // const wb = XLSX.utils.book_new();
  // valid.forEach((sheet) => {
  //   const headers = sheet.headers || [];
  //   const matrix = objectRowsToMatrix(headers, sheet.rows);
  //   const ws = XLSX.utils.aoa_to_sheet(matrix);
  //   XLSX.utils.book_append_sheet(wb, ws, sanitizeSheetName(sheet.name));
  // });
  // XLSX.writeFile(wb, filename);
};

export const downloadExcelSheet = (sheet, filename) => {
  downloadExcelWorkbook([sheet], filename);
};

const funnelExportRows = (steps) =>
  (steps || []).map((s) => ({
    Step: s.step,
    Users: s.count,
    "Conversion %": formatPct(s.conversion_rate ?? 0),
    "Drop-off %": formatPct(s.dropoff_rate ?? 0),
  }));

const retentionExportSheet = (cohorts) => {
  const list = cohorts || [];
  if (!list.length) return null;
  const maxPeriods = Math.max(...list.map((c) => (c.values || []).length), 0);
  const headers = ["Cohort", ...Array.from({ length: maxPeriods }, (_, i) => `Week ${i}`)];
  const rows = list.map((c) => {
    const row = { Cohort: c.cohort_date };
    (c.values || []).forEach((v, i) => {
      row[`Week ${i}`] = formatPct(typeof v === "object" ? v.rate : v);
    });
    return row;
  });
  return { name: "Retention Cohorts", headers, rows };
};

const trendExportRows = (points, valueKey, dateKey = "date") =>
  (points || []).map((p) => ({
    Date: p[dateKey],
    Value: p[valueKey],
  }));

const makeSheet = (name, headers, rows) =>
  rows?.length ? { name, headers, rows } : null;

export const buildDashboardExportSheets = ({
  data,
  KPI_DEFS = [],
  ENGAGEMENT_DEFS = [],
  trendMode = "daily",
  normalizedSources = [],
  normalizedPlatforms = [],
  signupFunnelSteps = [],
  funnelSteps = [],
  leadToInvestorSteps = [],
  signupSummary = {},
  signupStepCompletions = [],
  topProducts = [],
  compareEnabled = false,
  params = {},
}) => {
  if (!data) return {};

  const sheets = {};
  const stamp = dayjs().format("YYYY-MM-DD");
  const periodLabel = params.from && params.to ? `${params.from}_${params.to}` : stamp;

  const kpiRows = KPI_DEFS.map(({ key, label, format: fmt = formatNumber }) => {
    const k = data.kpis?.[key] || {};
    return {
      Metric: label,
      Value: fmt(k.value),
      "Change %": compareEnabled && k.change_pct != null ? `${k.change_pct}%` : "",
    };
  });
  sheets.kpis = makeSheet("KPIs", ["Metric", "Value", "Change %"], kpiRows);

  KPI_DEFS.forEach(({ key, label, format: fmt = formatNumber }) => {
    const k = data.kpis?.[key] || {};
    sheets[`kpi-${key}`] = makeSheet(label, ["Metric", "Value", "Change %"], [
      {
        Metric: label,
        Value: fmt(k.value),
        "Change %": compareEnabled && k.change_pct != null ? `${k.change_pct}%` : "",
      },
    ]);
  });

  const trend = data.active_users_trend || {};
  if (trendMode === "weekly") {
    sheets["active-users"] = makeSheet(
      "Active Users Weekly",
      ["Week", "WAU"],
      trendExportRows(trend.wau, "wau")
    );
  } else {
    const dauRows = (trend.dau || []).map((d, i) => ({
      Date: d.date,
      DAU: d.dau,
      MAU: trend.mau?.[Math.min(i, (trend.mau?.length || 1) - 1)]?.mau ?? "",
    }));
    sheets["active-users"] = makeSheet("Active Users Daily", ["Date", "DAU", "MAU"], dauRows);
  }

  sheets["acquisition-sources"] = makeSheet(
    "Acquisition Sources",
    ["Source", "Users", "Share %"],
    normalizedSources.map((s) => ({
      Source: s.label,
      Users: s.users,
      "Share %": formatPct(s.pct || 0),
    }))
  );

  sheets.platform = makeSheet(
    "Platform Breakdown",
    ["Platform", "Users", "Share %"],
    normalizedPlatforms.map((p) => ({
      Platform: p.label,
      Users: p.users,
      "Share %": formatPct(p.pct || 0),
    }))
  );

  const installTrend = data.installs_trend || {};
  const installPoints = trendMode === "weekly" ? installTrend.weekly : installTrend.daily;
  sheets["installs-trend"] = makeSheet(
    trendMode === "weekly" ? "Install Trend Weekly" : "Install Trend Daily",
    ["Date", "Installs"],
    trendExportRows(installPoints, "installs")
  );

  sheets.attribution = makeSheet(
    "Attribution Summary",
    ["Source", "Users", "Share %"],
    (data.acquisition?.summary || []).map((r) => ({
      Source: r.label,
      Users: r.count,
      "Share %": formatPct(r.pct || 0),
    }))
  );

  sheets.auth = makeSheet(
    "Auth Performance",
    ["Metric", "Count", "Change %"],
    (data.auth_performance_table || []).map((r) => ({
      Metric: r.metric,
      Count: r.cur,
      "Change %": r.change_pct != null ? `${r.change_pct}%` : "",
    }))
  );

  sheets["signup-summary"] = makeSheet(
    "Signup Summary",
    ["Metric", "Value", "Note"],
    [
      { Metric: "Flow started", Value: signupSummary.flow_started, Note: signupSummary.flow_started_unique },
      { Metric: "Completed", Value: signupSummary.completed, Note: formatPct(signupSummary.completion_rate || 0) },
      { Metric: "Abandoned", Value: signupSummary.abandoned, Note: formatPct(signupSummary.abandon_rate || 0) },
      { Metric: "Failed", Value: signupSummary.failed, Note: "validation / API errors" },
      { Metric: "Drop-off (no register)", Value: signupSummary.drop_off_without_registration, Note: "" },
    ]
  );

  sheets["signup-funnel"] = makeSheet(
    "Signup Funnel",
    ["Step", "Users", "Conversion %", "Drop-off %"],
    funnelExportRows(signupFunnelSteps)
  );

  const extendedSignupSteps = filterSignupFunnelSteps(data?.signup_funnel?.extended_funnel || []);
  if (extendedSignupSteps.length) {
    sheets["signup-funnel-extended"] = makeSheet(
      "Signup Funnel Extended",
      ["Step", "Users", "Conversion %", "Drop-off %"],
      funnelExportRows(extendedSignupSteps)
    );
  }

  sheets["step-completions"] = makeSheet(
    "Signup Step Completions",
    ["Step", "Completions"],
    signupStepCompletions.map((r) => ({ Step: r.label, Completions: r.count }))
  );

  const engKpis = data.engagement_kpis || {};
  sheets["engagement-kpis"] = makeSheet(
    "Engagement KPIs",
    ["Metric", "Value", "Change %"],
    ENGAGEMENT_DEFS.map(({ key, label, format: fmt }) => {
      const k = engKpis[key] || {};
      const val = k.value ?? data.engagement?.[key];
      return {
        Metric: label,
        Value: fmt ? fmt(val) : formatNumber(val),
        "Change %": k.change_pct != null ? `${k.change_pct}%` : "",
      };
    })
  );

  sheets["attributed-installs"] = makeSheet(
    "Attributed Installs",
    ["Channel", "Installs"],
    (data.attributed_installs || []).map((r) => ({ Channel: r.channel, Installs: r.count }))
  );

  sheets["investment-funnel"] = makeSheet(
    "Investment Funnel",
    ["Step", "Users", "Conversion %", "Drop-off %"],
    funnelExportRows(funnelSteps)
  );

  sheets["top-screens"] = makeSheet(
    "Top Screens",
    ["Screen", "Views", "Users", "Avg Time"],
    (data.top_screens || []).map((r) => ({
      Screen: r.screen,
      Views: r.views,
      Users: r.users,
      "Avg Time": formatDuration(r.avg_time_sec),
    }))
  );

  sheets.engagement = makeSheet(
    "Lead to Investor Funnel",
    ["Step", "Users", "Conversion %", "Drop-off %"],
    funnelExportRows(leadToInvestorSteps)
  );

  const retentionSheet = retentionExportSheet(data.retention?.cohorts);
  if (retentionSheet) sheets.retention = retentionSheet;

  sheets["hot-leads"] = makeSheet(
    "Hot Leads",
    ["Product", "Drop Step", "Amount", "Last Seen", "User", "Action"],
    (data.hot_leads?.rows || []).map((r) => ({
      Product: formatProductLabel(r.product_name),
      "Drop Step": r.drop_step,
      Amount: r.amount,
      "Last Seen": r.last_seen ? dayjs(r.last_seen).format("YYYY-MM-DD HH:mm") : "",
      User: r.user?.email || [r.user?.first_name, r.user?.last_name].filter(Boolean).join(" ") || r.distinct_id,
      Action: r.action,
    }))
  );

  sheets.products = makeSheet(
    "Top Products",
    ["Product", "Views", "CTA Clicks", "Investments", "Conv. Rate"],
    topProducts.map((r) => ({
      Product: formatProductLabel(r.product),
      Views: r.views,
      "CTA Clicks": r.cta_clicks,
      Investments: r.investments,
      "Conv. Rate": formatPct(r.conv_rate || 0),
    }))
  );

  sheets["campaign-roi"] = makeSheet(
    "Campaign ROI",
    ["Source", "Attributed Users", "Signups", "Investments", "Conv. Rate"],
    (data.campaign_roi?.rows || []).map((r) => ({
      Source: r.source,
      "Attributed Users": r.attributed_users,
      Signups: r.signups,
      Investments: r.investments,
      "Conv. Rate": formatPct(r.conv_rate || 0),
    }))
  );

  sheets["re-engagement"] = makeSheet(
    "Re-engagement",
    ["Cohort", "Users", "Suggested Action"],
    (data.reengagement || []).map((r) => ({
      Cohort: r.cohort,
      Users: r.users,
      "Suggested Action": r.suggested_action,
    }))
  );

  sheets.events = makeSheet(
    "Events Overview",
    ["Category", "Events", "Trend (daily counts)"],
    (data.events_overview || []).map((r) => ({
      Category: r.category,
      Events: r.count,
      "Trend (daily counts)": (r.sparkline || []).join("|"),
    }))
  );

  sheets.realtime = makeSheet(
    "Latest Activity",
    ["Time", "Event", "Label", "Platform"],
    (data.realtime_activity || []).map((r) => ({
      Time: r.timestamp ? dayjs(r.timestamp).format("YYYY-MM-DD HH:mm:ss") : "",
      Event: r.event,
      Label: r.label || r.event,
      Platform: r.platform,
    }))
  );

  const geo = data.geo_summary || {};
  sheets["geo-countries"] = makeSheet(
    "Geo Countries",
    ["Country", "Users", "Events"],
    (geo.countries || []).map((r) => ({
      Country: r.country,
      Users: r.users,
      Events: r.events,
    }))
  );

  sheets["geo-cities"] = makeSheet(
    "Geo Cities",
    ["Country", "City", "Users", "Lat", "Lng"],
    (geo.cities || []).map((r) => ({
      Country: r.country,
      City: r.city,
      Users: r.users,
      Lat: r.lat,
      Lng: r.lng,
    }))
  );

  sheets.alerts = makeSheet(
    "Alerts",
    ["Severity", "Title", "Message"],
    (data.alerts || []).map((r) => ({
      Severity: r.severity,
      Title: r.title,
      Message: r.message,
    }))
  );

  sheets.errors = makeSheet(
    "Top Errors",
    ["Type", "Message", "Count", "Users", "Last Seen"],
    (data.errors || []).map((r) => ({
      Type: r.type,
      Message: r.message,
      Count: r.count,
      Users: r.users,
      "Last Seen": r.last_seen ? dayjs(r.last_seen).format("YYYY-MM-DD HH:mm") : "",
    }))
  );

  const dq = data.data_quality || {};
  sheets["data-quality"] = makeSheet(
    "Data Quality",
    ["Metric", "Count"],
    [
      { Metric: "Total events", Count: dq.total },
      { Metric: "Good events", Count: dq.good_events },
      { Metric: "Dropped (errors)", Count: dq.dropped },
      { Metric: "Duplicate", Count: dq.duplicate },
      { Metric: "Good %", Count: formatPct(dq.good_pct ?? 0) },
    ]
  );

  sheets._meta = { periodLabel, stamp };
  return sheets;
};

export const buildDetailExportSheet = (detailKey, data, extra) => {
  const baseSheets = buildDashboardExportSheets({
    data,
    KPI_DEFS: [],
    signupFunnelSteps: filterSignupFunnelSteps(data?.signup_funnel?.funnel || []),
    funnelSteps: data?.investment_funnel?.funnel || [],
    leadToInvestorSteps: data?.engagement_funnel?.funnel || [],
    topProducts: data?.top_products || [],
  });

  if (baseSheets[detailKey]) return baseSheets[detailKey];

  switch (detailKey) {
    case "drop-offs": {
      const signupAbandoned = extra?.signup_abandoned || [];
      const signupFailed = extra?.signup_failures || [];
      const items = extra?.drop_offs || extra?.items || [];
      const rows = [
        ...signupAbandoned.map((r) => ({
          Type: "Signup abandon",
          Step: r.label,
          Count: r.count,
          "Avg time on step": r.avg_time_on_step_ms ? formatDuration(r.avg_time_on_step_ms / 1000) : "",
        })),
        ...signupFailed.map((r) => ({
          Type: "Signup failure",
          Step: r.label,
          Count: r.count,
          "Avg time on step": "",
        })),
        ...items.map((r) => ({
          Type: "Investment drop-off",
          Step: r.step,
          Count: r.dropped,
          "Avg time on step": formatPct(r.dropoff_rate || 0),
        })),
      ];
      return makeSheet("Drop-offs", ["Type", "Step", "Count", "Avg time on step"], rows);
    }
    default:
      return null;
  }
};

export const exportAllDashboardExcel = (sheets, periodLabel) => {
  const list = Object.entries(sheets || {})
    .filter(([key]) => !key.startsWith("_"))
    .map(([, sheet]) => sheet)
    .filter((s) => s?.rows?.length);
  // downloadExcelWorkbook(list, `analytics-dashboard_${periodLabel || "export"}.xlsx`);
};

export const exportSheetCsv = (sheet, slug) => {
  const name = sanitizeSheetName(sheet?.name || slug).replace(/\s+/g, "-").toLowerCase();
  downloadCsvSheet(sheet, `${name}.csv`);
};

export const exportSheetExcel = (sheet, slug) => {
  const name = sanitizeSheetName(sheet?.name || slug).replace(/\s+/g, "-").toLowerCase();
  // downloadExcelSheet(sheet, `${name}.xlsx`);
};

const USER_EXPORT_HEADERS = [
  "Distinct ID",
  "User ID",
  "Name",
  "Email",
  "Mobile",
  "Device",
  "Platform",
  "City",
  "Country",
  "Events",
  "Last Seen",
];

const FUNNEL_USER_BASE_HEADERS = [
  "Distinct ID",
  "User ID",
  "Name",
  "Email",
  "Mobile",
  "Device",
  "Platform",
  "City",
  "Country",
  "Last Funnel Step",
];

const resolveExportUserId = (row) => {
  if (row.user?.id) return row.user.id;
  const raw = row.signup_user_id || row.event_user_id || "";
  const parsed = parseInt(String(raw).trim(), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : "";
};

const formatAudienceUserRow = (row) => ({
  "Distinct ID": row.distinct_id || "",
  "User ID": resolveExportUserId(row),
  Name: [row.user?.first_name, row.user?.last_name].filter(Boolean).join(" ") || "",
  Email: row.user?.email || row.signup_email || "",
  Mobile: row.user?.mobile || row.user?.phone || "",
  Device: row.device_brand || row.device || "",
  Platform: row.platform || "",
  City: row.city || "",
  Country: row.country || "",
  Events: row.events_count ?? "",
  "Last Seen": row.last_seen ? dayjs(row.last_seen).format("YYYY-MM-DD HH:mm:ss") : "",
});

const formatFunnelMatrixUserRow = (row, stepLabels) => {
  const base = {
    "Distinct ID": row.distinct_id || "",
    "User ID": resolveExportUserId(row),
    Name: [row.user?.first_name, row.user?.last_name].filter(Boolean).join(" ") || "",
    Email: row.user?.email || row.signup_email || "",
    Mobile: row.user?.mobile || row.user?.phone || "",
    Device: row.device_brand || row.device || "",
    Platform: row.platform || "",
    City: row.city || "",
    Country: row.country || "",
    "Last Funnel Step": row.last_funnel_step || "",
  };

  stepLabels.forEach((label) => {
    base[label] = row.funnel_step_values?.[label] || "No";
  });

  base["Last Seen"] = row.last_seen ? dayjs(row.last_seen).format("YYYY-MM-DD HH:mm:ss") : "";
  return base;
};

export const exportFunnelStepsUsersExcel = async ({
  params,
  widgetKey,
  stepLabels,
  fetchWidgetDetail,
  fileSlug,
}) => {
  if (!params || !fetchWidgetDetail || !stepLabels?.length) return false;

  const sheets = [];
  for (let i = 0; i < stepLabels.length; i += 1) {
    const label = stepLabels[i];
    const res = await fetchWidgetDetail({
      ...params,
      widget: resolveWidgetExportKey(widgetKey),
      export_users: "1",
      export_funnel_step: i,
      export_funnel_step_label: label,
    });
    const sheet = audienceToUserExportSheet(res?.result, `${label} Users`);
    if (sheet?.rows?.length) sheets.push(sheet);
  }

  if (!sheets.length) return false;
  // downloadExcelWorkbook(sheets, `${fileSlug || widgetKey}-funnel-users.xlsx`);
  return true;
};

export const audienceToUserExportSheet = (audience, sheetName) => {
  const stepLabels = audience?.funnel_step_labels || [];
  const isFunnelMatrix =
    audience?.export_mode === "funnel_matrix" ||
    (stepLabels.length > 0 && audience?.users?.[0]?.funnel_step_values);

  const rows = isFunnelMatrix
    ? (audience?.users || []).map((row) => formatFunnelMatrixUserRow(row, stepLabels))
    : (audience?.users || []).map(formatAudienceUserRow);

  if (!rows.length) return null;

  const headers = isFunnelMatrix
    ? [...FUNNEL_USER_BASE_HEADERS, ...stepLabels, "Last Seen"]
    : USER_EXPORT_HEADERS;

  const scopeSuffix = audience?.export_scope ? ` (${audience.export_scope})` : "";
  return {
    name: sanitizeSheetName(`${sheetName || `${audience?.widget || "Widget"} Users`}${scopeSuffix}`),
    headers,
    rows,
  };
};

export const resolveWidgetExportKey = (slug) => {
  const key = String(slug || "").trim();
  if (key.startsWith("kpi-")) return key;
  const aliases = {
    "signup-summary": "signup-funnel",
    "engagement-kpis": "engagement",
    "geographic-insights": "geo-countries",
  };
  return aliases[key] || key;
};

export const hotLeadsToUserExportSheet = (leadRows, sheetName = "Hot Leads Users") => {
  if (!leadRows?.length) return null;
  const headers = [
    "Distinct ID",
    "User ID",
    "Name",
    "Email",
    "Mobile",
    "Product",
    "Drop Step",
    "Amount",
    "Last Seen",
    "Action",
  ];
  const rows = leadRows.map((r) => ({
    "Distinct ID": r.distinct_id || "",
    "User ID": r.user?.id ?? "",
    Name: [r.user?.first_name, r.user?.last_name].filter(Boolean).join(" ") || "",
    Email: r.user?.email || "",
    Mobile: r.user?.mobile || r.user?.phone || "",
    Product: formatProductLabel(r.product_name),
    "Drop Step": r.drop_step || "",
    Amount: r.amount ?? "",
    "Last Seen": r.last_seen ? dayjs(r.last_seen).format("YYYY-MM-DD HH:mm:ss") : "",
    Action: r.action || "",
  }));
  return { name: sanitizeSheetName(sheetName), headers, rows };
};

export const DASHBOARD_USER_EXPORT_KEYS = [
  "kpis",
  "active-users",
  "acquisition-sources",
  "platform",
  "installs-trend",
  "attribution",
  "auth",
  "signup-funnel",
  "step-completions",
  "engagement-kpis",
  "attributed-installs",
  "investment-funnel",
  "top-screens",
  "engagement",
  "retention",
  "products",
  "campaign-roi",
  "re-engagement",
  "events",
  "realtime",
  "geo-countries",
  "alerts",
  "errors",
  "data-quality",
  "drop-offs",
];

export const OVERVIEW_EXPORT_KEYS = [
  "kpis",
  "active-users",
  "acquisition-sources",
  "platform",
  "installs-trend",
];

export const exportSectionDashboardExcel = (exportSheets, widgetKeys, sectionSlug, periodLabel) => {
  const list = (widgetKeys || [])
    .map((key) => exportSheets?.[key])
    .filter((s) => s?.rows?.length);
  if (!list.length) return false;
  // downloadExcelWorkbook(list, `analytics-${sectionSlug}_${periodLabel || "export"}.xlsx`);
  return true;
};

export const exportSectionUsersExcel = async ({
  params,
  exportSheets,
  widgetKeys,
  sectionSlug,
  fetchWidgetDetail,
}) => {
  if (!params || !fetchWidgetDetail || !widgetKeys?.length) return false;

  const periodLabel =
    exportSheets?._meta?.periodLabel || `${params.from || "from"}_${params.to || "to"}`;

  const results = await Promise.allSettled(
    widgetKeys.map(async (key) => {
      const res = await fetchWidgetDetail({
        ...params,
        widget: resolveWidgetExportKey(key),
        export_users: "1",
      });
      const label = exportSheets?.[key]?.name || key;
      return audienceToUserExportSheet(res?.result, `${label} Users`);
    })
  );

  const sheets = [];
  results.forEach((result) => {
    if (result.status === "fulfilled" && result.value?.rows?.length) {
      sheets.push(result.value);
    }
  });

  if (!sheets.length) return false;
  // downloadExcelWorkbook(sheets, `analytics-${sectionSlug}-users_${periodLabel}.xlsx`);
  return true;
};

export const exportAllDashboardUsersExcel = async ({
  params,
  hotLeadsRows,
  exportSheets,
  fetchWidgetDetail,
}) => {
  if (!params || !fetchWidgetDetail) return false;

  const periodLabel =
    exportSheets?._meta?.periodLabel || `${params.from || "from"}_${params.to || "to"}`;

  const sheets = [];
  if (hotLeadsRows?.length) {
    const hotSheet = hotLeadsToUserExportSheet(hotLeadsRows);
    if (hotSheet) sheets.push(hotSheet);
  }

  const results = await Promise.allSettled(
    DASHBOARD_USER_EXPORT_KEYS.map(async (key) => {
      const res = await fetchWidgetDetail({
        ...params,
        widget: resolveWidgetExportKey(key),
        export_users: "1",
      });
      const label = exportSheets?.[key]?.name || key;
      return audienceToUserExportSheet(res?.result, `${label} Users`);
    })
  );

  results.forEach((result) => {
    if (result.status === "fulfilled" && result.value?.rows?.length) {
      sheets.push(result.value);
    }
  });

  if (!sheets.length) return false;
  // downloadExcelWorkbook(sheets, `analytics-users_${periodLabel}.xlsx`);
  return true;
};
